import { inject, Injectable } from '@angular/core';
import {
  FunctionBlockConfigEvent,
  FunctionBlockStateEvent,
  NetworkDeviceStateEvent,
} from '@ubie/mqttcommunication';
import {
  combineLatest,
  delay,
  filter,
  map,
  merge,
  Observable,
  of,
  OperatorFunction,
  startWith,
  takeUntil,
} from 'rxjs';

import { DeviceCommunicationService } from '../../../classes/communicationStoreObjects/device-communication-service';
import { FunctionBlockCommunicationService } from '../../../classes/communicationStoreObjects/function-block-communication-service';
import { NetworkDeviceCommunicationService } from '../../../classes/communicationStoreObjects/network-device-communication-service';
import {
  CommunicationFunctionBlockKey,
  CommunicationNetworkDeviceKey,
  NetworkDeviceOnlineState,
  UbieState,
} from '../../../interfaces';
import { getUniqueObjectsByProperties } from '../../../utility/get-unique-objects-by-properties.interface';
import { LogService } from '../../log/log.service';
/**
 * the service for the communication store
 * this service is responsible to setup a filter for the communication
 * and then give you the subscription to listen on for the events
 * you need.
 */
@Injectable({
  providedIn: 'root',
})
export class CommunicationService {
  private readonly deviceCommunicationService = inject(
    DeviceCommunicationService,
  );
  private readonly networkDeviceCommunicationService = inject(
    NetworkDeviceCommunicationService,
  );

  private readonly functionBlockCommunicationService = inject(
    FunctionBlockCommunicationService,
  );

  private readonly logService = inject(LogService);

  private setTimeOutPipe<T>(
    source: Observable<T>,
    timeOutInMs: number,
    defaultValue: T,
  ): Observable<T> {
    // Create a delayed "heartbeat" that only emits once
    const timeoutPlaceholder$ = of(defaultValue).pipe(
      delay(timeOutInMs),
      // If the source emits before this delay ends,
      // we want to ignore this placeholder entirely.
      takeUntil(source),
    );

    return merge(source, timeoutPlaceholder$);
  }

  /**
   * checks if the ubie is connected
   * @param ubieSerialNumber the ubie serial number to check
   * @param timeOutInMs the time out in ms to wait for the ubie state
   * @returns an observable with a boolean that indicates if the ubie is connected
   *  this observable will timeout if the ubie state is not available within the given time
   *  and will return false if it does
   */
  isUbieConnected$(
    ubieSerialNumber: string,
    timeOutInMs = 5000,
  ): Observable<boolean> {
    return this.getUbieState$(ubieSerialNumber, timeOutInMs).pipe(
      map((ubieState) => ubieState.isConnected),
    );
  }
  /**
   * get the last states for the ubies
   * @param ubieSerialNumbers the ubie serialNumbers that you want the states for
   * @returns an observable with a ubie state event or undefined if something isn't correct
   **/
  getUbieStates$(
    ubieSerialNumbers: string[],
    timeOutInMs = 5000,
  ): Observable<UbieState[]> {
    ubieSerialNumbers = [...new Set(ubieSerialNumbers)];

    const observables: Observable<UbieState>[] = ubieSerialNumbers.map(
      (ubieSerialNumber) =>
        this.getUbieState$(ubieSerialNumber, timeOutInMs).pipe(
          startWith({
            isConnected: false,
            isOfflineMode: false,
            serial: ubieSerialNumber,
          }),
        ),
    );

    return combineLatest([...observables]);
  }

  getUbieState$(
    ubieSerialNumber: string,
    timeOutInMs = 5000,
  ): Observable<UbieState> {
    const sub = merge(
      this.deviceCommunicationService.enable(ubieSerialNumber),
      this.deviceCommunicationService.getOrAddItem(ubieSerialNumber),
    ).pipe(
      filter((state) => Boolean(state as UbieState)) as OperatorFunction<
        void | UbieState,
        UbieState
      >,
    );

    return this.setTimeOutPipe(sub, timeOutInMs, {
      isConnected: false,
      isOfflineMode: false,
      serial: ubieSerialNumber,
    });
  }

  // #endregion Ubie Functions

  // #region Network Device Functions
  /**
   *  get the state for a network device
   * @param ubieSerialNumber  the serial number of the ubie
   * @param networkDeviceSerialNumber the serial number of the network device
   * @returns  an observable with a network device state event
   */
  getNetworkDeviceState$(
    ubieSerialNumber: string,
    networkDeviceSerialNumber: string,
    timeOutInMs = 5000,
  ): Observable<NetworkDeviceStateEvent> {
    const key = `${ubieSerialNumber}/${networkDeviceSerialNumber}`;

    const sub = this.getNetworkDeviceStateSubscription(key);

    return this.setTimeOutPipe(sub, timeOutInMs, {
      connectable: false,
      connected: false,
      networkDeviceId: networkDeviceSerialNumber,
      ubieId: ubieSerialNumber,
    });
  }

  private getNetworkDeviceStateSubscription(
    key: string,
  ): Observable<NetworkDeviceStateEvent> {
    const sub = merge(
      this.networkDeviceCommunicationService.enable(key),
      this.networkDeviceCommunicationService.getOrAddItem(key),
    ).pipe(
      filter((state) =>
        Boolean(state as NetworkDeviceStateEvent),
      ) as OperatorFunction<
        void | NetworkDeviceStateEvent,
        NetworkDeviceStateEvent
      >,
    );

    return sub;
  }

  /**
   * Determines the online status of a network device associated with a specific Ubie device.
   *
   * This function checks both the connection status of the Ubie device and the
   * specific network device to determine if both are connected and the network device is
   * connectable. If either the Ubie device or the network device is not in a suitable
   * connection state, the observable emits `false`.
   *
   *
   * @param ubieSerialNumber - The serial number of the Ubie device.
   * @param networkDeviceSerialNumber - The serial number of the network device.
   * @param timeOutInMs - (Optional) The timeout period in milliseconds before giving up on
   *                      obtaining the network device state, defaulting to 10,000 ms.
   * @returns An observable that emits a boolean indicating the online status of the
   *          network device.
   */
  isNetworkDeviceOnline$(
    ubieSerialNumber: string,
    networkDeviceSerialNumber: string,
    timeOutInMs = 10000,
  ): Observable<boolean> {
    const ubieState$ = this.setTimeOutPipe(
      this.deviceCommunicationService.getOrAddItem(ubieSerialNumber),
      timeOutInMs,
      {
        isConnected: false,
        isOfflineMode: false,
        serial: ubieSerialNumber,
      },
    ).pipe(map((ubieState) => ubieState.isConnected));

    const networkDeviceState$ = this.getNetworkDeviceState$(
      ubieSerialNumber,
      networkDeviceSerialNumber,
      timeOutInMs,
    ).pipe(map((networkDeviceState) => networkDeviceState.connected));

    return combineLatest([ubieState$, networkDeviceState$]).pipe(
      map(([isUbieOnline, isNetworkDeviceOnline]) => {
        if (isUbieOnline === false) return false;

        return isNetworkDeviceOnline;
      }),
    );
  }

  areNetworkDevicesOnline$(
    networkDevices: CommunicationNetworkDeviceKey[],
    timeOutInMs = 5000,
  ): Observable<NetworkDeviceOnlineState[]> {
    networkDevices = getUniqueObjectsByProperties(networkDevices, [
      'ubieSerialNumber',
      'networkDeviceSerialNumber',
    ]);
    return combineLatest([
      ...networkDevices.map((networkDevice) => {
        return this.isNetworkDeviceOnline$(
          networkDevice.ubieSerialNumber ?? '',
          networkDevice.networkDeviceSerialNumber ?? '',
          timeOutInMs,
        ).pipe(
          startWith(undefined),
          map((isOnline) => {
            return {
              ubieSerialNumber: networkDevice.ubieSerialNumber ?? '',
              networkDeviceSerialNumber:
                networkDevice.networkDeviceSerialNumber ?? '',
              isOnline,
            } as NetworkDeviceOnlineState;
          }),
        );
      }),
    ]);
  }

  // #endregion

  // #region Function Block Functions

  getFunctionBlockCommunication$(
    ubieSerialNumber: string,
    networkDeviceSerialNumber: string,
    functionBlockSerialNumber: string,
  ): Observable<FunctionBlockStateEvent | FunctionBlockConfigEvent> {
    const id = `${ubieSerialNumber}/${networkDeviceSerialNumber}/${functionBlockSerialNumber}`;

    return merge(
      this.functionBlockCommunicationService.enable(id),
      this.functionBlockCommunicationService.getOrAddItem(id),
    ).pipe(
      filter((state) =>
        Boolean(
          (state as FunctionBlockConfigEvent) ||
            (state as FunctionBlockStateEvent),
        ),
      ) as OperatorFunction<
        void | FunctionBlockConfigEvent | FunctionBlockStateEvent,
        FunctionBlockConfigEvent | FunctionBlockStateEvent
      >,
    );
  }

  /**
   * get the state for a function block
   * @param ubieSerialNumber the serial number of the ubie
   * @param networkDeviceSerial the serial number of the network device
   * @param functionblockSerial the serial number of the function block
   * @returns an observable with a function block state event or undefined if something isn't correct
   */
  getFunctionBlockState$(
    ubieSerialNumber: string,
    networkDeviceSerialNumber: string,
    functionBlockSerialNumber: string,
    timeOutInMs = 10000,
  ): Observable<FunctionBlockStateEvent> {
    const sub = this.getFunctionBlockCommunication$(
      ubieSerialNumber,
      networkDeviceSerialNumber,
      functionBlockSerialNumber,
    );

    return this.setTimeOutPipe(sub, timeOutInMs, {
      ubieId: ubieSerialNumber,
      networkDeviceId: networkDeviceSerialNumber,
      functionBlockId: functionBlockSerialNumber,
      updatedProperties: new Set<string>(),
      properties: {},
    } as FunctionBlockStateEvent).pipe(
      filter((event: FunctionBlockStateEvent | FunctionBlockConfigEvent) => {
        return 'updatedProperties' in event;
      }) as OperatorFunction<
        FunctionBlockStateEvent | FunctionBlockConfigEvent,
        FunctionBlockStateEvent
      >,
    );
  }

  getFunctionBlockConfig$(
    ubieSerialNumber: string,
    networkDeviceSerialNumber: string,
    functionBlockSerialNumber: string,
    timeOutInMs = 10000,
  ): Observable<FunctionBlockConfigEvent> {
    const sub = this.getFunctionBlockCommunication$(
      ubieSerialNumber,
      networkDeviceSerialNumber,
      functionBlockSerialNumber,
    );

    return this.setTimeOutPipe(sub, timeOutInMs, {
      ubieId: ubieSerialNumber,
      networkDeviceId: networkDeviceSerialNumber,
      functionBlockId: functionBlockSerialNumber,
      config: '',
    } as FunctionBlockConfigEvent).pipe(
      filter((event: FunctionBlockStateEvent | FunctionBlockConfigEvent) => {
        return 'config' in event;
      }) as OperatorFunction<
        FunctionBlockStateEvent | FunctionBlockConfigEvent,
        FunctionBlockConfigEvent
      >,
    );
  }

  getFunctionBlockStateForMultipleFunctionBlocks$(
    functionblocks: CommunicationFunctionBlockKey[],
    timeOutInMs = 5000,
  ): Observable<FunctionBlockStateEvent[]> {
    functionblocks = getUniqueObjectsByProperties(functionblocks, [
      'ubieSerialNumber',
      'networkDeviceSerialNumber',
      'functionBlockSerialNumber',
    ]);

    return combineLatest([
      ...functionblocks.map((functionblock) => {
        return this.getFunctionBlockState$(
          functionblock.ubieSerialNumber,
          functionblock.networkDeviceSerialNumber,
          functionblock.functionBlockSerialNumber,
          timeOutInMs,
        ).pipe(
          startWith({
            ubieId: functionblock.ubieSerialNumber,
            networkDeviceId: functionblock.networkDeviceSerialNumber,
            functionBlockId: functionblock.functionBlockSerialNumber,
            updatedProperties: new Set<string>([]),
            properties: {},
          } as FunctionBlockStateEvent),
        );
      }),
    ]);
  }

  getFunctionBlockConfigForMultipleFunctionBlocks$(
    functionblocks: CommunicationFunctionBlockKey[],
    timeOutInMs = 10000,
  ): Observable<FunctionBlockConfigEvent[]> {
    functionblocks = getUniqueObjectsByProperties(functionblocks, [
      'ubieSerialNumber',
      'networkDeviceSerialNumber',
      'functionBlockSerialNumber',
    ]);
    return combineLatest([
      ...functionblocks.map((functionblock) => {
        return this.getFunctionBlockConfig$(
          functionblock.ubieSerialNumber,
          functionblock.networkDeviceSerialNumber,
          functionblock.functionBlockSerialNumber,
          timeOutInMs,
        );
      }),
    ]);
  }

  getFunctionBlockConfigForMultipleFunctionBlocksSeparated$(
    functionblocks: CommunicationFunctionBlockKey[],
    timeOutInMs = 10000,
  ): Observable<FunctionBlockConfigEvent> {
    functionblocks = getUniqueObjectsByProperties(functionblocks, [
      'ubieSerialNumber',
      'networkDeviceSerialNumber',
      'functionBlockSerialNumber',
    ]);
    return merge(
      ...functionblocks.map((functionblock) => {
        return this.getFunctionBlockConfig$(
          functionblock.ubieSerialNumber,
          functionblock.networkDeviceSerialNumber,
          functionblock.functionBlockSerialNumber,
          timeOutInMs,
        );
      }),
    );
  }

  // #endregion
}
